import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import { setAuthToken, getMe } from '../services/api';
import type { Team, TeamSettings } from '../types';

interface AuthContextValue {
  session: Session | null;
  user: any | null;
  team: Team | null;
  settings: TeamSettings | null;
  loading: boolean;
  hasTeam: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<any | null>(null);
  const [team, setTeam] = useState<Team | null>(null);
  const [settings, setSettings] = useState<TeamSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    try {
      const me = await getMe();
      setUser(me.user);
      setTeam(me.team);
      setSettings(me.settings);
    } catch {
      // user has a session but no team yet (needs to register/join a team)
      setUser(null);
      setTeam(null);
      setSettings(null);
    }
  }, []);

  const applySession = useCallback(
    async (s: Session | null) => {
      setSession(s);
      setAuthToken(s?.access_token ?? null);
      if (s) await loadMe();
      else {
        setUser(null);
        setTeam(null);
        setSettings(null);
      }
    },
    [loadMe]
  );

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(async ({ data }) => {
      if (!mounted) return;
      await applySession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, s) => {
      await applySession(s);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [applySession]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user,
      team,
      settings,
      loading,
      hasTeam: !!team,
      signIn,
      signUp,
      signOut,
      refreshUser: loadMe,
    }),
    [session, user, team, settings, loading, signIn, signUp, signOut, loadMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
